import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import Gyanbeej from '../media/LOGO Gyanbeej transparent.png';
import heroImg from '../media/generated/gyanbeej-hero.webp';
import modesImg from '../media/generated/gyanbeej-modes.webp';
import audienceImg from '../media/generated/gyanbeej-audience.webp';
import { Section, SectionHeading, PageHero, Card, IconBadge, Steps, Faq, CtaBand, fadeUp } from '../components/ui';
import { ArrowUpRightIcon, CheckIcon, BookIcon, ChatIcon, BoltIcon, MicIcon, BrainIcon, SparkleIcon } from '../components/icons';

const SITE = 'https://www.gyanbeej.com/';

const SUBJECTS = [
  { group: 'Science', items: ['Physics', 'Chemistry', 'Biology'] },
  { group: 'Mathematics', items: ['Algebra', 'Geometry', 'Statistics'] },
  { group: 'Social Science', items: ['History', 'Geography', 'Civics'] },
];

const Stats = () => (
  <section className="bg-white/[0.04] border-b border-white/10 py-10">
    <div className="container mx-auto px-[5%]">
      <div className="grid grid-cols-3 max-w-2xl mx-auto gap-6 text-center">
        {[
          ['500+', 'Students'],
          ['95%', 'Success rate'],
          ['24/7', 'AI support'],
        ].map(([num, label]) => (
          <motion.div key={label} {...fadeUp}>
            <div className="text-3xl md:text-4xl font-extrabold text-purple-400">{num}</div>
            <div className="text-sm text-gray-500 mt-1">{label}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Subjects = () => (
  <Section>
    <SectionHeading
      eyebrow="Coverage"
      title="Aligned to the NCERT curriculum, Class 6–10"
      subtitle="Gyanbeej works from the textbooks students are actually taught and tested on."
    />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
      {SUBJECTS.map((s, i) => (
        <motion.div key={s.group} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }} className="p-6 rounded-2xl bg-white/[0.04] border border-white/10">
          <h3 className="font-bold text-white mb-4">{s.group}</h3>
          <ul className="flex flex-wrap gap-2">
            {s.items.map((item) => (
              <li key={item} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white/[0.04] text-purple-300 border border-purple-400/20">
                {item}
              </li>
            ))}
          </ul>
        </motion.div>
      ))}
    </div>
  </Section>
);

const CoreFeature = () => (
  <Section tone="raised">
    <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
      <motion.div {...fadeUp}>
        <span className="text-sm font-bold tracking-widest uppercase text-purple-400">Most popular</span>
        <h2 className="text-2xl md:text-4xl font-extrabold mt-3 mb-5 text-white">AI-powered interactive learning</h2>
        <p className="text-lg text-gray-400 leading-relaxed mb-8">
          Get instant, personalized explanations for any NCERT concept. Students ask in their own words —
          and follow up as many times as they need until the idea actually clicks.
        </p>
        <div className="grid grid-cols-2 gap-4">
          {[
            { icon: BoltIcon, title: 'Instant responses', desc: 'No waiting for a doubt session.' },
            { icon: BrainIcon, title: 'Personalized', desc: 'Explanations adapt to the learner.' },
            { icon: SparkleIcon, title: 'Concept clarity', desc: 'Built to explain, not just answer.' },
            { icon: ChatIcon, title: '24/7 available', desc: 'Study whenever it suits you.' },
          ].map((item) => (
            <div key={item.title} className="p-4 rounded-xl bg-white/[0.04] border border-white/10">
              <IconBadge icon={item.icon} className="mb-3" />
              <h3 className="font-bold text-white text-sm mb-1">{item.title}</h3>
              <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </motion.div>
      <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
        <img src={modesImg} alt="Illustration of interactive study mode cards" className="w-full max-w-sm mx-auto" />
      </motion.div>
    </div>
  </Section>
);

const StudyModes = () => (
  <Section>
    <SectionHeading
      eyebrow="Learning zone"
      title="Four ways to learn every concept"
      subtitle="Different concepts need different approaches. Gyanbeej gives students four, and lets them switch freely."
    />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
      {[
        { icon: BookIcon, title: 'Story Cards', sub: 'Learn through stories', desc: 'Concepts broken into narrative flip cards that make abstract ideas concrete.' },
        { icon: CheckIcon, title: 'Quiz Mode', sub: 'Test your knowledge', desc: 'Chapter-aligned multiple-choice questions with instant feedback and score tracking.' },
        { icon: BoltIcon, title: 'Rapid Fire', sub: 'Speed round mastery', desc: '15-second timed challenges that build the recall speed exams demand.' },
        { icon: MicIcon, title: 'Voice Mode', sub: 'Speak & learn', desc: 'Real-time voice conversations for hands-free, back-and-forth study.' },
      ].map((item, i) => (
        <motion.div
          key={item.title}
          {...fadeUp}
          transition={{ duration: 0.5, delay: i * 0.08 }}
          className="p-6 rounded-2xl bg-white/[0.04] border border-white/10 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          <IconBadge icon={item.icon} className="mb-4" />
          <h3 className="font-bold text-white">{item.title}</h3>
          <p className="text-xs font-semibold text-purple-400 mb-2">{item.sub}</p>
          <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
        </motion.div>
      ))}
    </div>
  </Section>
);

const FeatureGrid = () => (
  <Section tone="raised">
    <SectionHeading eyebrow="More features" title="Everything built into Gyanbeej" />
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
      {[
        ['NCERT Aligned', 'Every answer maps back to the prescribed textbook.'],
        ['Interactive Learning', 'Ask follow-ups until the concept is clear.'],
        ['Visual Learning', 'Diagrams and illustrations alongside explanations.'],
        ['Audio Explanations', 'Listen to concepts while on the go.'],
        ['Smart Flowcharts', 'Auto-generated flowcharts for processes and cycles.'],
        ['Instant Responses', 'Answers in seconds, any time of day.'],
      ].map(([title, desc], i) => (
        <motion.div key={title} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.05 }} className="p-5 rounded-2xl bg-white/[0.04] border border-white/10">
          <CheckIcon className="w-4 h-4 text-purple-400 mb-2" />
          <h3 className="font-bold text-white text-sm mb-1">{title}</h3>
          <p className="text-xs text-gray-400 leading-relaxed">{desc}</p>
        </motion.div>
      ))}
    </div>
  </Section>
);

const Audience = () => (
  <Section>
    <SectionHeading
      eyebrow="Built for every learning journey"
      title="Students, teachers, and parents"
      subtitle="One product, three different jobs to be done."
    />
    <motion.div {...fadeUp} className="mb-12">
      <img src={audienceImg} alt="Illustration of three platforms for students, teachers and parents" className="w-full max-w-2xl mx-auto" />
    </motion.div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
      {[
        { title: 'Students', icon: ChatIcon, points: ['Doubt-solving conversations', 'Interactive study modes', 'Visual aids and flowcharts', 'Progress tracking'] },
        { title: 'Teachers', icon: BookIcon, points: ['Lesson planning support', 'Assessment creation', 'STEM model explanations', 'Ready teaching resources'] },
        { title: 'Parents', icon: CheckIcon, points: ['Curriculum-aligned content', 'Learning insights', 'Homework assistance', 'Guidance at home'] },
      ].map((col) => (
        <Card key={col.title} hover>
          <IconBadge icon={col.icon} className="mb-4" />
          <h3 className="font-bold text-white mb-3">{col.title}</h3>
          <ul className="space-y-2">
            {col.points.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-gray-400">
                <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-purple-500" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  </Section>
);

const HowItWorks = () => (
  <Section tone="raised">
    <SectionHeading eyebrow="Getting started" title="Up and running in three steps" />
    <div className="max-w-4xl mx-auto">
      <Steps
        steps={[
          { title: 'Create a free account', desc: 'Sign up on gyanbeej.com — no setup, no installation needed.' },
          { title: 'Pick your class and chapter', desc: 'Choose from NCERT Class 6 to 10 across Science, Maths, and Social Science.' },
          { title: 'Ask, practise, revise', desc: 'Chat through doubts, then switch to Story Cards, quizzes, or voice to lock the concept in.' },
        ]}
      />
    </div>
  </Section>
);

const GyanbeejFaq = () => (
  <Section>
    <SectionHeading eyebrow="FAQ" title="Questions about Gyanbeej" />
    <Faq
      items={[
        {
          q: 'Which classes does Gyanbeej support?',
          a: 'Gyanbeej supports NCERT curriculum from Class 6 to Class 10, covering Science (Physics, Chemistry, Biology), Mathematics (Algebra, Geometry, Statistics), and Social Science (History, Geography, Civics).',
        },
        {
          q: 'Is Gyanbeej aligned to the NCERT syllabus?',
          a: 'Yes. Gyanbeej is built specifically around NCERT textbooks, so explanations, quizzes, and practice questions map back to what students are actually taught and assessed on.',
        },
        {
          q: 'What are the four study modes?',
          a: 'Story Cards turn concepts into narratives; Quiz Mode gives chapter-aligned multiple-choice practice with instant feedback; Rapid Fire runs 15-second timed challenges to build recall speed; and Voice Mode lets students learn through real-time spoken conversation.',
        },
        {
          q: 'Can teachers and parents use Gyanbeej too?',
          a: 'Yes. Teachers use it for lesson planning, assessment creation, and teaching resources. Parents use it to support homework, understand how their child is progressing, and access curriculum-aligned content at home.',
        },
        {
          q: 'Does Gyanbeej work on mobile?',
          a: 'Yes, Gyanbeej runs in the browser on phones, tablets, and computers, so students can study on whichever device they have access to.',
        },
        {
          q: 'How much does Gyanbeej cost?',
          a: 'You can get started for free. Current pricing details are listed on the Gyanbeej website.',
        },
      ]}
    />
  </Section>
);

const ProductGyanbeej = () => (
  <>
    <Seo
      title="Gyanbeej — AI Learning Assistant for NCERT Students"
      description="Gyanbeej is an AI learning assistant for NCERT textbooks, Class 6-10. Interactive doubt-solving, Story Cards, Quiz Mode, Rapid Fire, and Voice Mode for students, teachers, and parents."
      path="/products/gyanbeej"
    />
    <PageHero
      eyebrow="Gyanbeej"
      title="Your AI Learning Assistant for NCERT Textbooks"
      subtitle="Get instant help with your NCERT textbooks from Class 6 to 10 — across Science, Mathematics, and Social Science."
      image={heroImg}
      imageAlt="Illustration of a mobile AI study assistant"
    >
      <div className="flex flex-col sm:flex-row gap-4">
        <a
          href={SITE}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-white text-[#1a0033] font-semibold hover:bg-purple-500/10 transition-colors"
        >
          Get started for free <ArrowUpRightIcon className="w-4 h-4" />
        </a>
        <Link
          to="/products"
          className="px-8 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors text-center"
        >
          Compare products
        </Link>
      </div>
      <img src={Gyanbeej} alt="Gyanbeej logo" className="h-9 object-contain mt-10" />
    </PageHero>
    <Stats />
    <Subjects />
    <CoreFeature />
    <StudyModes />
    <FeatureGrid />
    <Audience />
    <HowItWorks />
    <GyanbeejFaq />
    <CtaBand title="Ready to transform your learning?" subtitle="Create a free account and start with your first chapter today.">
      <a
        href={SITE}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-[#1a0033] font-semibold hover:bg-purple-500/10 transition-colors"
      >
        Get started for free <ArrowUpRightIcon className="w-4 h-4" />
      </a>
    </CtaBand>
  </>
);

export default ProductGyanbeej;
